import { CommonModule } from '@angular/common'
import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet'
import { MatButtonModule } from '@angular/material/button'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'
import { TranslationService } from '../../../../../../services/translation.service'
import {
  ImportComponentGroup,
  ImportValidationStatus,
} from '../../../../../../types/componentGroups'
import { ImportComponent } from '../../../../../../types/components'

@Component({
  selector: 'app-components-sheet',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './components-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './components-sheet.component.scss',
})
export class ComponentsSheetComponent {
  translationService = inject(TranslationService)
  private _bottomSheetRef =
    inject<MatBottomSheetRef<ComponentsSheetComponent>>(MatBottomSheetRef)
  data = inject<{
    componentGroups: Array<ImportComponentGroup>
    components: Array<ImportComponent>
  }>(MAT_BOTTOM_SHEET_DATA)

  displayedColumns: Array<string> = [
    'componentSku',
    'componentName',
    'supplier',
    'quantity',
    'validationStatus',
  ]

  groups: Array<ImportComponentGroup> = []

  constructor() {
    const data = this.data

    this.groups = (data.componentGroups || []).map((g) => this.toGroup(g))
  }

  close(): void {
    this._bottomSheetRef.dismiss()
  }

  groupTitle(name: string): string {
    if (!name) {
      return this.translationService.getUngroupedComponentsText()
    }
    return name
  }

  statusLabel(validationStatus: ImportValidationStatus): string {
    if (validationStatus === 'invalid') {
      return this.translationService.getInvalidText()
    }
    return this.translationService.getValidText()
  }

  private toComponent(c: Partial<ImportComponent>): ImportComponent {
    return {
      parentSku: c?.parentSku || '',
      componentSku: c?.componentSku || '',
      componentName: c?.componentName || '',
      componentGroupName: c?.componentGroupName || '',
      supplier: c?.supplier ?? null,
      quantity: c?.quantity || 0,
      validationStatus:
        (c?.validationStatus as ImportValidationStatus) || 'invalid',
    }
  }

  private toGroup(g: Partial<ImportComponentGroup>): ImportComponentGroup {
    const components = (g?.components || []).map((c) => this.toComponent(c))

    const status =
      (g?.validationStatus as ImportValidationStatus) ||
      (components.some((c) => c.validationStatus === 'invalid')
        ? 'invalid'
        : 'valid')

    return {
      name: g?.name || '',
      components,
      validationStatus: status,
    }
  }
}
